<template>
    <section class="indication container pt-2">
        <div class="row m-0">
            <div class="col-6">
                <h5 class="pb-2 lh-25"><b>indication</b></h5>
                <p>
                    JORNAY PM (methylphenidate HCl extended-release capsules) is
                    a central nervous system (CNS) stimulant indicated for the
                    treatment of Attention Deficit Hyperactivity Disorder (ADHD)
                    in patients 6 years and older.
                </p>
                <p style="text-decoration: underline;">Limitations of Use:
                </p>
                <p>
                    The use of JORNAY PM is not recommended in pediatric patients 
                    younger than 6 years of age because they had higher plasma exposure 
                    and a higher incidence of adverse reactions (e.g., weight loss) 
                    than patients 6 years and older at the same dosage.
                </p>
                <h5 class="pb-2 lh-25"><b>CONTRAINDICATIONS</b></h5>

                <ul class="mb-0">
                    <li>
                        Known hypersensitivity to methylphenidate or other
                        components of JORNAY PM. Hypersensitivity reactions such
                        as angioedema and anaphylactic reactions have been
                        reported in patients treated with methylphenidate
                        products.
                    </li>
                    <li>
                        Concurrent treatment with a monoamine oxidase inhibitor
                        (MAOI), or use of an MAOI within the preceding 14 days
                        because of the risk of hypertensive crisis.
                    </li>
                </ul>

                <h5 class="pb-2 lh-25"><b>WARNINGS AND PRECAUTIONS</b></h5>
                <p>
                    <i
                        >JORNAY PM can cause serious adverse reactions and
                        patients should be monitored for the following:</i
                    >
                </p>
                <ul class="mb-2 d-block d-lg-none">
                    <li>
                        <i>Risks to Patients with Serious Cardiac Disease:</i>
                        Sudden death has been reported in patients with
                        structural cardiac abnormalities or other serious
                        cardiac disease who were treated with CNS stimulants at
                        the recommended ADHD dosage. Avoid use in patients with
                        known structural cardiac abnormalities, cardiomyopathy,
                        serious cardiac arrhythmia, coronary artery disease, or
                        other serious cardiac disease.
                    </li>
                </ul>
            </div>
            <div class="col-6">
                <h5 class="pb-2 lh-25"><b>IMPORTANT SAFETY INFORMATION</b></h5>

                <div class="box mb-2 mb-lg-0">
                    <p class="mb-2">
                        <b>BOXED WARNING: ABUSE, MISUSE, AND ADDICTION</b>
                    </p>
                    <p class="mb-2">
                        <b
                            >JORNAY PM has a high potential for abuse and
                            misuse, which can lead to the development of a
                            substance use disorder, including addiction. Misuse
                            and abuse of CNS stimulants, including JORNAY PM,
                            can result in overdose and death, and this risk is
                            increased with higher doses or unapproved methods of
                            administration, such as snorting or injection.
                            Before prescribing JORNAY PM, assess each patient’s
                            risk for abuse, misuse, and addiction.</b
                        >
                    </p>
                    <p class="mb-2">
                        <b
                            >Educate patients and their families about these
                            risks, proper storage of the drug, and proper
                            disposal of any unused drug. Throughout JORNAY PM
                            treatment, reassess each patient’s risk of abuse,
                            misuse, and addiction and frequently monitor for
                            signs and symptoms of abuse, misuse and
                            addiction.</b
                        >
                    </p>
                </div>
            </div>

            <div class="col-12 d-none d-lg-block">
                <ul class="mb-2">
                    <li>
                        <i>Risks to Patients with Serious Cardiac Disease:</i>
                        Sudden death has been reported in patients with
                        structural cardiac abnormalities or other serious
                        cardiac disease who were treated with CNS stimulants at
                        the recommended ADHD dosage. Avoid use in patients with
                        known structural cardiac abnormalities, cardiomyopathy,
                        serious cardiac arrhythmia, coronary artery disease, or
                        other serious cardiac disease.
                    </li>
                </ul>
            </div>

            <div class="col-12">
                <ul class="mb-2">
                    <li><i>Increased Blood Pressure and Heart Rate</i>.</li>
                    <li>
                        <i>Psychiatric Adverse Reactions:</i> Including
                        exacerbation of behavior disturbance and thought
                        disorder in patients with a pre-existing psychotic
                        disorder, induction of a manic episode in patients with
                        bipolar disorder, and new psychotic or manic symptoms.
                        Prior to initiating treatment, screen patients for risk
                        factors for psychiatric adverse reactions. If such
                        symptoms occur, consider discontinuing <span class="text-nowrap">JORNAY PM.</span>
                    </li>
                    <li>
                        <i>Priapism:</i> Patients should seek immediate medical
                        attention.
                    </li>
                    <li>
                        <i
                            >Peripheral Vasculopathy, including Raynaud’s
                            Phenomenon:</i
                        >
                        Observe patients for digital changes during treatment.
                    </li>
                    <li>
                        <i
                            >Weight Loss and Long-Term Suppression of Growth in
                            Pediatric Patients:</i
                        >
                        Monitor height and weight. JORNAY PM is not approved for use and is not recommended in pediatric patients below 6 years of age.
                    </li>
                    <li>
                        <i
                            >Increased Intraocular Pressure (IOP) and
                            Glaucoma:</i
                        >
                        Patients at risk for acute angle closure glaucoma should
                        be evaluated by an ophthalmologist. Closely monitor
                        patients with a history of abnormally increased IOP or
                        open angle glaucoma.
                    </li>
                    <li>
                        <i
                            >Onset or Exacerbation of Motor and Verbal Tics and
                            Worsening of Tourette’s Syndrome</i
                        >.
                    </li>
                </ul>
            </div>

            <div class="col-12">
                <h5 class="pb-2 lh-25"><b>ADVERSE REACTIONS</b></h5>
                <ul class="mb-2">
                    <li>
                        The most common (≥5% and twice the rate of placebo)
                        adverse reactions with methylphenidate are decreased
                        appetite, insomnia, nausea, vomiting, dyspepsia,
                        abdominal pain, decreased weight, anxiety, dizziness,
                        irritability, affect lability, tachycardia, and
                        increased blood pressure.
                    </li>
                    <li>
                        Additional adverse reactions (≥5% and twice the rate of
                        placebo) in JORNAY PM-treated pediatric patients 6 to 12
                        years are headache, psychomotor hyperactivity, and mood
                        swings.
                    </li>
                </ul>
                <h5 class="pb-2 lh-25"><b>DRUG INTERACTIONS</b></h5>
                <ul>
                    <li>
                        Antihypertensive drugs: Monitor blood pressure; adjust
                        dosage of antihypertensive drug as needed.
                    </li>
                </ul>
            </div>
        </div>

        <div class="pt-0 mb-3 textisi">
            <p>
                <b
                    >To report SUSPECTED ADVERSE REACTIONS, contact Collegium
                    Pharmaceutical, Inc. at 1-855-331-5615 or FDA at
                    1-800-FDA-1088 or </b
                ><b
                    ><a
                        style="color: #2da7df"
                        href="https://www.fda.gov/medwatch"
                        target="_blank"
                        >www.fda.gov/medwatch</a
                    ></b
                >.
            </p>

            <p>
                <b style="color:#30368C;">
                    Please see Important Safety Information and Full Prescribing
                    Information including Boxed Warning on Abuse, Misuse, and
                    Addiction and other serious risks, at
                    <a
                        href="https://www.jornaypm-pro.com/pi"
                        target="_blank"
                        data-category="Global ISI"
                        style="color: #2da7df"
                        >JORNAYPM-pro.com/pi</a
                    >
                </b>
            </p>
        </div>

        <div class="d-flex justify-content-end mt-4 yellow-top">
            <div class="yellow-container" @click="scrollToTop">
                <!-- <img
                    class="yellow-container-img"
                    src="../assets/img/top.svg"
                    alt="top button"
                /> -->
            </div>
        </div>

        <div v-if="!hideTitle">
            <div v-if="$route.name === 'home'" class="references pb-5 pt-4">
                <span
                    ><b>References: 1.</b> JORNAY PM<sup>®</sup> [package
                    insert]. Ironshore Pharmaceuticals & Development, Inc.
                    <b>2.</b> Childress AC, Cutler AJ, Marraﬃno A, et al. A
                    randomized, double-blind, placebo-controlled study of
                    HLD200, a delayed-release and extended-release
                    methylphenidate, in children with
                    attention-deﬁcit/hyperactivity disorder: an evaluation of
                    safety and eﬃcacy throughout the day and across settings.
                    <i>J Child Adolesc Psychopharmacol</i>. 2020;30(1):2-14.
                    doi:10.1089/cap.2019.0070 <b>3.</b> Pliszka SR, Wilens TE,
                    Bostrom S, et al. Efficacy and safety of HLD200,
                    delayed-release and extended-release methylphenidate, in
                    children with attention-deficit/hyperactivity disorder.
                    <i> J Child Adolesc Psychopharmacol</i>. 2017;27(6):474-482.
                    doi:10.1089/cap.2017.0084 <b>4.</b> Incledon B, Incledon C,
                    Gomeni R, et al. Effect of colonic absorption on the
                    pharmacokinetic properties of delayed-release and
                    extended-release methylphenidate: in vivo, in vitro, and
                    modeling evaluations. <i>Clin Pharmacol Drug Dev</i>.
                    2022;11(8):966-975. doi:10.1002/cpdd.1089 <b>5.</b> Po M,
                    Gomeni R, Incledon B. Quantitative characterization of the
                    smoothness of extended-release methylphenidate
                    pharmacokinetic profiles. <i>Innov Clin Neurosci</i>.
                    2022;19(7-9):32-37. <b> 6.</b> Gomeni R, Komolova M,
                    Incledon B, Faraone SV. Model-based approach for
                    establishing the predicted clinical response of a
                    delayed-release and extended-release methylphenidate for the
                    treatment of attention-deficit/hyperactivity disorder.
                    <i>J Clin Psychopharmacol</i>. 2020;40(4):350-358.
                    doi:10.1097/JCP.0000000000001222 <b>7.</b> Mercalis, data as
                    of February 25, 2025.</span
                >
                <b> 8.</b> Data on File. Collegium Pharmaceutical. 2025.
            </div>
            <div v-if="$route.name === 'safety'" class="references pb-5 pt-4">
                <span
                    ><b>References: 1.</b> Childress AC, Cutler AJ, Marraffino
                    A, et al. A randomized, double-blind, placebo-controlled
                    study of HLD200, a delayed-release and extended-release
                    methylphenidate, in children with
                    attention-deficit/hyperactivity disorder: an evaluation of
                    safety and efficacy throughout the day and across settings.
                    <i>J Child Adolesc Psychopharmacol</i>. 2020;30(1):2-14.
                    doi:10.1089/cap.2019.0070 <b>2.</b> JORNAY PM<sup>®</sup>
                    [package insert]. Ironshore Pharmaceuticals & Development,
                    Inc. <b>3.</b> Katzman MA, Otcheretko V, Po MD, Uchida CL,
                    Incledon B. Adverse events during dosing of
                    delayed-release/extended-release methylphenidate: learnings
                    from the open-label phase of a registration trial and a
                    real-world postmarketing surveillance program.
                    <em>Clin Ther</em>. 2023;45(12):1212-1221.
                    doi:10.1016/j.clinthera.2023.09.009
                    <strong>4.</strong> Pliszka SR, Wilens TE, Bostrom S, et al.
                    Efficacy and safety of HLD200, delayed-release and
                    extended-release methylphenidate, in children with
                    attention-deficit/hyperactivity disorder.
                    <i>J Child Adolesc Psychopharmacol</i>. 2017;27(6):474-482.
                    doi:10.1089/cap.2017.0084 <strong>5.</strong> Mercalis, data
                    as of February 25, 2025. <strong>6.</strong> Data on File.
                    Collegium Pharmaceutical. 2025.</span
                >
            </div>
            <div v-if="$route.name === 'dosing'" class="references pb-5 pt-4">
                <span
                    ><b>References: 1.</b> JORNAY PM<sup>®</sup> [package
                    insert]. Ironshore Pharmaceuticals & Development, Inc.
                    <strong>2.</strong> Childress AC, Cutler AJ, Marraffino A,
                    et al. A randomized, double-blind, placebo-controlled study
                    of HLD200, a delayed-release and extended-release
                    methylphenidate, in children with
                    attention-deficit/hyperactivity disorder: an evaluation of
                    safety and efficacy throughout the day and across settings.
                    <em>J Child Adolesc Psychopharmacol</em>. 2020;30(1):2-14.
                    doi:10.1089/cap.2019.0070 <strong>3.</strong> Data on File.
                    Collegium Pharmaceutical. 2025.</span
                >
            </div>
            <div
                v-if="$route.name === 'adhd-medication-efficacy'"
                class="references pb-5 pt-4"
            >
                <span>
                    <b>References: 1.</b> JORNAY PM<sup>®</sup> [package
                    insert]. Ironshore Pharmaceuticals & Development, Inc.
                    <b>2.</b> Childress AC, Cutler AJ, Marraffino A, et al. A
                    randomized, double-blind, placebo-controlled study of
                    HLD200, a delayed-release and extended-release
                    methylphenidate, in children with
                    attention-deficit/hyperactivity disorder: an evaluation of
                    safety and efficacy throughout the day and across settings.
                    <i>J Child Adolesc Psychopharmacol.</i> 2020;30(1):2-14.
                    doi:10.1089/cap.2019.0070 <b>3.</b> Pliszka SR, Wilens TE,
                    Bostrom S, et al. Efficacy and safety of HLD200,
                    delayed-release and extended-release methylphenidate, in
                    children with attention-deficit/hyperactivity disorder.
                    <i>J Child Adolesc Psychopharmacol</i>. 2017;27(6):474-482.
                    doi:10.1089/cap.2017.0084

                    <b>4.</b> Wigal SB, Gupta S, Guinta D, Swanson JM.
                    Reliability and validity of the SKAMP rating scale in a
                    laboratory school setting. <i>Psychopharmacol Bull</i>.
                    1998;34(1):47-53.

                    <b>5. </b>Kelsey DK, Summer CR, Casat CD, et al. Once-daily
                    atomoxetine treatment for children with
                    attention-deficit/hyperactivity disorder, including an
                    assessment of evening and morning behavior: a double-blind,
                    placebo-controlled trial. <i>Pediatrics</i>.
                    2004;114(1):e1-e8. doi:10.1542/peds.114.1.e1

                    <b>6.</b> Faraone SV, DeSousa NJ, Komolova M, Sallee FR,
                    Incledon B, Wilens TE. Functional impairment in youth with
                    ADHD: normative data and norm-referenced cutoff points for
                    the Before School Functioning Questionnaire and the Parent
                    Rating of Evening and Morning Behavior Scale, Revised.
                    <i>J Clin Psychiatry</i>. 2019;81(1):19m12956.
                    doi:10.4088/JCP.19m12956

                    <b>7.</b> Data on File. Collegium Pharmaceutical. 2025.
                </span>
            </div>
            <div
                v-if="$route.name === 'methylphenidate-dosage-duration'"
                class="references pb-5 pt-4"
            >
                <span>
                    <b>References: 1.</b> Gomeni R, Komolova M, Incledon B,
                    Faraone SV. Model-based approach for establishing the
                    predicted clinical response of a delayed-release and
                    extended-release methylphenidate for the treatment of
                    attention-deficit/hyperactivity disorder.<i>J Clin Psychopharmacol</i>. 2020;40(4):350-358. doi:10.1097/JCP.0000000000001222
                    <b>2.</b> Childress AC, Cutler AJ, Marraﬃno A, et al. A
                    randomized, double-blind, placebo-controlled study of
                    HLD200, a delayed-release and extended-release
                    methylphenidate, in children with
                    attention-deﬁcit/hyperactivity disorder: an evaluation of
                    safety and eﬃcacy throughout the day and across settings.
                    <i>J Child Adolesc Psychopharmacol</i>. 2020;30(1):2-14.
                    doi:10.1089/cap.2019.0070 
                    <b>3.</b> Liu T, Gobburu JVS, Po MD, et
                    al. Pharmacokinetics of HLD200, a delayed-release and extended-release 
                    methylphenidate: evaluation of dose proportionality, food effect, 
                    multiple-dose modeling, and comparative bioavailability 
                    with immediate-release methylphenidate in healthy adults. <i>J Child Adolesc Psychopharmacol</i>. 2019;29(3):181-191. doi:10.1089/cap.2018.0122
                    <b>4.</b> Po M, Gomeni R, Incledon
                    B. Quantitative characterization of the smoothness of
                    extended-release methylphenidate pharmacokinetic profiles.
                    <i>Innov Clin Neurosci</i>. 2022;19(7-9):32-37.
                </span>
            </div>
            <div v-if="$route.name === 'pk-data'" class="references pb-5 pt-4">
                <span>
                    <b>References: 1.</b> Gomeni R, Komolova M, Incledon B,
                    Faraone SV. Model-based approach for establishing the
                    predicted clinical response of a delayed-release and
                    extended-release methylphenidate for the treatment of
                    attention-deficit/hyperactivity disorder.<i>
                        J Clin Psychopharmacol</i
                    >. 2020;40(4):350-358. doi:10.1097/JCP.0000000000001222
                    <b>2.</b> Incledon B, Incledon C, Gomeni R, et al. Effect of
                    colonic absorption on the pharmacokinetic properties of
                    delayed-release and extended-release methylphenidate: in
                    vivo, in vitro, and modeling evaluations.
                    <i> Clin Pharmacol Drug Dev.</i> 2022;11(8):966-975.
                    doi:10.1002/cpdd.1089 <b>3.</b> Po M, Gomeni R, Incledon B.
                    Quantitative characterization of the smoothness of
                    extended-release methylphenidate pharmacokinetic profiles.
                    <i> Innov Clin Neurosci</i>. 2022;19(7-9):32-37.

                    <b>4.</b> Childress AC, Cutler AJ, Marraﬃno A, et al. A
                    randomized, double-blind, placebo-controlled study of
                    HLD200, a delayed-release and extended-release
                    methylphenidate, in children with
                    attention-deﬁcit/hyperactivity disorder: an evaluation of
                    safety and eﬃcacy throughout the day and across settings.
                    <i>J Child Adolesc Psychopharmacol.</i> 2020;30(1):2-14.
                    doi:10.1089/cap.2019.0070 <b>5.</b> Gomeni R, Tan DT, Sallee
                    RF, Incledon B. A model-based pharmacokinetic comparison of
                    delayed-release/extended release to extended-release
                    methylphenidates with immediate-release supplementation.
                    Poster presented at: American Professional Society of ADHD
                    and Related Disorders (APSARD) Annual Meeting; January
                    15-17, 2021; Virtual.
                </span>
            </div>

            <div
                v-if="$route.name.toLowerCase() === 'peerspectives'"
                class="references pb-5 pt-4"
            >
                <span
                    ><b>References: 1.</b> Mercalis, data as of February 25,
                    2025. <strong>2.</strong> Data on File. Collegium
                    Pharmaceutical. 2025.</span
                >
            </div>

            <div
                v-if="$route.name.toLowerCase() === 'savings'"
                class="references pb-5 pt-4"
            >
                <span
                    ><b>References: 1.</b> Mercalis, data as of February 25,
                    2025. <strong>2.</strong> Data on File. Collegium
                    Pharmaceutical. 2025.</span
                >
            </div>
        </div>
    </section>
</template>
<script>
import { appleOSdetectionMixin } from '../views/mixins/appleOSdetectionMixin';

export default {
    mixins: [appleOSdetectionMixin],
    props: ['hideTitle'],

    methods: {
        scrollToTop() {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
    },
};
</script>

<style lang="scss" scoped>
.apple-only {
    .certain-b-spacing {
        letter-spacing: -0.7px !important;

        a {
            font-weight: 900 !important;
            letter-spacing: -0.7px !important;
        }
    }
}

.references {
    b,
    span,
    i {
        color: #414042 !important;
    }
}

.widget-wrapper.expanded {
    .yellow-top {
        opacity: 0;
        overflow: hidden;
        height: 0;
    }
}

.box {
    border: 1px solid #414042;
    padding: 10px;

    @media screen and (min-width: 992px) {
        padding: 20px;
        
    }

    p {
        color: #414042;
    }
}

.yellow-container {
    cursor: pointer;
    transform: translate(0%, 0%);
    height: 60px;
    width: 90px;
    background-image: url('../assets/img/yellow-btn.png');

    @media screen and (min-width: 1281px) {
        transform: translate(110%, 0%);
    }
}

.yellow-container:hover {
    background-image: url('../assets/img/yellow-btn-hover.png');
}

.indication {
    p,
    li,
    i {
        color: #414042;
    }

    h5 > b {
        font-size: 16px;
    }
}
</style>
